// src/components/weeklyReport/AddToRegxModal.js
//
// "Add to RegX" (2026-10-09): from the weekly report's "patient who isn't in
// RegX" form, add that patient to RegX in a box the coordinator picks. Same
// fields and checks as the Coordinator page's Add Patient (name, DOB, 9-digit
// MRN, at least one diagnosis; an MRN already in RegX is refused, a likely
// match is a warning with "Add anyway").
//
// Enrolled follows the patient page: pick the study (the report's study is
// ticked to start), the pick goes to an admin for approval, and if every study
// is diagnostics or closed the patient goes to Lung Center Referrals instead.
// The save itself is regxPatientCreate.js. Own classes (regx-add-*) with
// light-mode rules in index.css. The page mounts it only while open, so each
// opening starts from the report's typed name, DOB and MRN.

import { useMemo, useRef, useState } from "react";
import { UserPlus, Loader2, AlertTriangle } from "lucide-react";
import { WrModal, BTN_PRIMARY, BTN_SOFT, BTN_SMALL, INPUT, LABEL } from "./ui";
import SearchableSelect from "../SearchableSelect";
import EnrollStudyPickerModal from "../EnrollStudyPickerModal";
import PotentialDuplicateDialog from "../PotentialDuplicateDialog";
import { DIAGNOSIS_OPTIONS, diagnosisLabel } from "../../constants/diseaseOptions";
import { normalizeMrn9 } from "../../services/mrn";
import { ENROLLED_STATUS, REFERRALS_STATUS } from "../../services/enrollmentRules";
import {
  regxBoxOptions,
  validateNewPatient,
  planEnrollment,
  createRegxPatient,
  DuplicatePatientError,
} from "../../services/regxPatientCreate";
import { auth } from "../../firebase";

const startForm = (initial) => {
  const typed = String(initial?.mrn || "").trim();
  const nine = normalizeMrn9(typed).mrn9;
  return {
    name: initial?.name || "",
    dob: initial?.dob || "",
    mrn: nine || typed,
    diagnosis: [],
    status: REFERRALS_STATUS,
  };
};

const AddToRegxModal = ({
  open,
  initial, // { name, dob, mrn } from the report's form
  reportStudyId, // the study this report entry is for (pre-ticked for Enrolled)
  studies = [], // active studies
  coordinatorNames = [],
  onCancel,
  onAdded, // ({ id, record }) => void
  onUseExisting, // (patient) => void — an MRN already in RegX
}) => {
  const [form, setForm] = useState(() => startForm(initial));
  const [showErrors, setShowErrors] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "studies"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sameMrn, setSameMrn] = useState([]);
  const [likely, setLikely] = useState([]);
  const enrollmentRef = useRef(null);

  const boxes = useMemo(() => regxBoxOptions(coordinatorNames), [coordinatorNames]);
  const labelOf = useMemo(() => new Map(boxes.map((b) => [b.value, b.label])), [boxes]);
  const valueOf = useMemo(() => new Map(boxes.map((b) => [b.label, b.value])), [boxes]);
  const studiesById = useMemo(() => new Map(studies.map((s) => [s.id, s])), [studies]);
  const reportStudy = studiesById.get(reportStudyId);

  const errors = validateNewPatient(form);
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleDx = (dx) =>
    update({ diagnosis: form.diagnosis.includes(dx) ? form.diagnosis.filter((d) => d !== dx) : [...form.diagnosis, dx] });

  const save = async (allowLikely = false) => {
    setBusy(true);
    setError("");
    try {
      const result = await createRegxPatient(form, { allowLikely, enrollment: enrollmentRef.current });
      setLikely([]);
      onAdded?.(result);
    } catch (err) {
      if (err instanceof DuplicatePatientError) {
        setSameMrn(err.result.sameMrn);
        setLikely(err.result.sameMrn.length ? [] : err.result.likely);
        setStep("form");
      } else {
        console.error("❌ Could not add the patient to RegX:", err);
        setError("Couldn't add the patient to RegX. Please try again.");
        setStep("form");
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    if (errors.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setSameMrn([]);
    if (form.status === ENROLLED_STATUS) {
      setStep("studies"); // pick the study first, as on the patient page
      return;
    }
    enrollmentRef.current = null;
    save(false);
  };

  return (
    <>
      <WrModal
        open={open && step === "form"}
        onClose={() => !busy && onCancel?.()}
        icon={UserPlus}
        title="Add this patient to RegX"
        subtitle="Same as Add Patient on the Coordinator page, in the box you pick"
        footer={
          <>
            <button type="button" className={`${BTN_SOFT} mr-auto`} onClick={onCancel} disabled={busy}>
              Back
            </button>
            <button type="button" className={BTN_PRIMARY} onClick={submit} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {form.status === ENROLLED_STATUS ? "Next: pick the study" : "Add to RegX"}
            </button>
          </>
        }
      >
        <div className="regx-add space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-3">
              <span className={LABEL}>Patient name</span>
              <input value={form.name} onChange={(e) => update({ name: e.target.value })} className={INPUT} autoComplete="off" />
            </label>
            <label className="block">
              <span className={LABEL}>Date of birth</span>
              <input type="date" value={form.dob} onChange={(e) => update({ dob: e.target.value })} className={INPUT} />
            </label>
            <label className="block sm:col-span-2">
              <span className={LABEL}>MRN (9 digits)</span>
              <input
                value={form.mrn}
                onChange={(e) => update({ mrn: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                className={INPUT}
                inputMode="numeric"
                placeholder="9 digits"
                autoComplete="off"
              />
            </label>
          </div>

          <div>
            <span className={LABEL}>Diagnosis (at least one)</span>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Diagnosis">
              {DIAGNOSIS_OPTIONS.map((dx) => {
                const on = form.diagnosis.includes(dx);
                return (
                  <button
                    key={dx}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleDx(dx)}
                    className={`weekly-report-option px-2.5 py-1 rounded-lg border text-[12px] font-semibold transition ${
                      on
                        ? "weekly-report-option--on border-purple-400/50 bg-purple-500/15 text-white"
                        : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-slate-300"
                    }`}
                  >
                    {diagnosisLabel(dx)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className={LABEL}>Box on the Coordinator page</span>
            <SearchableSelect
              value={labelOf.get(form.status) || form.status}
              options={boxes.map((b) => b.label)}
              onChange={(label) => update({ status: valueOf.get(label) || label })}
              placeholder="Type to find a box…"
              optionIcon={UserPlus}
            />
            {form.status === ENROLLED_STATUS && (
              <p className="regx-add-note mt-1.5 text-[11px] text-slate-400">
                Next you pick the study they're enrolled in{reportStudy ? ` (${reportStudy.NAME || reportStudy.STUDY_ID} is ticked)` : ""}.
                It goes to an admin for approval, as on the patient page.
              </p>
            )}
          </div>

          {sameMrn.length > 0 && (
            <div className="weekly-report-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200 space-y-2">
              <p className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                This MRN is already in RegX, so the patient can't be added again.
              </p>
              {sameMrn.slice(0, 3).map((p) => (
                <div key={`${p.source}:${p.id}`} className="flex flex-wrap items-center gap-2">
                  <span className="flex-1 min-w-0">
                    {p.fullName || p.name || "(no name)"} · DOB {p.dob || "—"} · {p.sourceLabel}
                  </span>
                  <button type="button" className={BTN_SMALL} onClick={() => onUseExisting?.(p)}>
                    Use that patient
                  </button>
                </div>
              ))}
            </div>
          )}

          {showErrors && errors.length > 0 && (
            <ul className="weekly-report-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200 list-disc pl-6">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          {error && (
            <p className="weekly-report-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
              {error}
            </p>
          )}
        </div>
      </WrModal>

      {/* Enrolled: the patient page's study picker, report's study ticked. */}
      <EnrollStudyPickerModal
        open={open && step === "studies"}
        studies={studies}
        studiesById={studiesById}
        currentEntries={[]}
        initialPicks={reportStudy ? [reportStudy] : []}
        onCancel={() => setStep("form")}
        onConfirm={(picks) => {
          enrollmentRef.current = planEnrollment(picks, studiesById, {
            at: new Date().toISOString(),
            by: auth.currentUser?.email || "unknown",
          });
          setStep("saving");
          save(false);
        }}
      />

      {/* Likely match: same warning as Add Patient, "Add anyway" re-saves. */}
      <PotentialDuplicateDialog
        matches={likely}
        entered={{ name: form.name, dob: form.dob, mrn: form.mrn }}
        onCancel={() => setLikely([])}
        onProceed={() => {
          if (window.confirm("Add this patient anyway? A possible duplicate was found with the same date of birth.")) {
            setLikely([]);
            save(true);
          }
        }}
      />
    </>
  );
};

export default AddToRegxModal;
